// Cellery Soapbox — shared data contract.
// These shapes MUST match what scripts/publish.mjs writes and what
// functions/ and firestore.rules enforce. Do not diverge field names.

import type { Timestamp } from "firebase/firestore";

/** Firestore: docs/{docId} */
export interface DocMeta {
  docId: string;
  title: string;
  slug: string;
  ownerUid: string | null; // reserved provenance; currently always null (owner access is via members)
  ownerEmail: string; // lowercased @cellery.ai
  currentVersion: string; // e.g. "v3"
  viewers: string[]; // lowercased emails (direct grants on this doc)
  editors: string[]; // lowercased emails (direct grants on this doc)
  members: string[]; // EFFECTIVE read set: union(ownerEmail, viewers, editors, inherited folder grants); the ACL-read key
  editorMembers?: string[]; // EFFECTIVE editor set: union(ownerEmail, editors, inherited folder editors). Missing on legacy docs → fall back to `editors`.
  folderId?: string | null; // parent folder (null/absent = root). Set by createDoc / organize (Admin SDK).
  trusted?: boolean; // GLOBAL trust: true = scripts run for EVERY reader. Browser uploads are false → viewer sandboxes the iframe. Missing → treated as trusted (legacy CLI docs). Owner-only, via POST /trust. See PersonalTrust for the per-account opt-in.
  createdAt: Timestamp; // set once at creation
  updatedAt: Timestamp; // bumped on every publish (create + re-publish)
  draft?: DraftMeta | null; // present while an editor has an unpublished draft
  stats?: ViewStats; // usage rollup; missing on docs nobody has opened yet ⇒ treat as zeros
}

/**
 * Firestore: docs/{docId}.stats — denormalized usage summary (CONTRACTS §4).
 * Lives ON the doc so the workspace list renders counts with no extra reads, and
 * so any member who can read the doc can see the aggregate. Written ONLY by the
 * Admin SDK (recordView / saveDraft / commitVersion); `updatedAt` is deliberately
 * NOT touched by view recording — that field means "bumped on every publish".
 * The per-person breakdown lives in the canEdit-gated `viewers`/`views`
 * subcollections (ViewerStat / ViewSession).
 */
export interface ViewStats {
  views: number; // total view sessions across everyone
  viewers: number; // unique accounts that have opened the doc
  totalMs: number; // total ACTIVE reading time (tab visible and not idle)
  lastViewedAt?: Timestamp;
  lastViewedByEmail?: string; // lowercased
  commits: number; // versions published in-app via commitVersion
  lastEditedAt?: Timestamp;
  lastEditedByEmail?: string; // lowercased
}

/** Firestore: docs/{docId}/viewers/{uid} — per-account rollup (read: canEdit). */
export interface ViewerStat {
  uid: string;
  email: string; // lowercased
  views: number;
  totalMs: number;
  firstViewedAt?: Timestamp;
  lastViewedAt?: Timestamp;
}

/**
 * Firestore: docs/{docId}/views/{sessionId} — one reading session, powering the
 * activity timeline (read: canEdit). `ms` is absolute accumulated active time,
 * re-set on every heartbeat, so a retried beat can never double-count.
 */
export interface ViewSession {
  sessionId: string;
  uid: string;
  email: string; // lowercased
  versionId: string;
  ms: number;
  startedAt?: Timestamp;
  lastBeatAt?: Timestamp;
}

/**
 * Firestore: docs/{docId}/trust/{uid} — PERSONAL trust (CONTRACTS §6c).
 * Lifts the untrusted-content sandbox for THIS ACCOUNT ONLY, so any member may
 * grant it to themselves: the only session exposed to the bundle's JS is their
 * own. Distinct from DocMeta.trusted, which is global and owner-only. Written by
 * the client under an own-uid rule (like annotations/comments), never by a script.
 */
export interface PersonalTrust {
  uid: string;
  email: string; // lowercased
  trustedAt: Timestamp;
}

/**
 * Firestore: folders/{folderId} — a sharing container that nests. ACL shape
 * mirrors DocMeta. `members`/`editorMembers` are EFFECTIVE (own grants unioned
 * with every ancestor's), denormalized server-side so doc-list and the rules
 * `list` stay a single statically-provable array-contains query. Written only by
 * the createFolder / setFolderAccess / organize endpoints (Admin SDK); clients
 * never write folders/*.
 */
export interface FolderMeta {
  folderId: string;
  name: string;
  slug: string;
  parentId: string | null; // null = root folder
  ownerUid: string | null; // reserved provenance; null today (access via members)
  ownerEmail: string; // lowercased @cellery.ai
  viewers: string[]; // direct grants on this folder
  editors: string[]; // direct grants on this folder
  members: string[]; // EFFECTIVE read set (self + ancestor grants); the ACL-read key
  editorMembers: string[]; // EFFECTIVE editor set (self + ancestor editor grants)
  ancestors: string[]; // ancestor folderIds, root-first (breadcrumb + subtree + cycle guard)
  depth: number; // 0 for a root folder; ancestors.length
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/**
 * Firestore: docs/{docId}.draft — pointer to the shared, in-progress edit of the
 * current version. The edited entry bytes live in Storage at
 * docs/{docId}/draft/{entry}; promoting via /commit mints a new version and
 * clears this. Written only by the saveDraft/commit endpoints (Admin SDK).
 */
export interface DraftMeta {
  baseVersion: string; // the version this draft forks from (== currentVersion)
  entry: string; // entry file path being edited, e.g. "index.html"
  bytes: number; // size of the edited entry HTML
  updatedAt: Timestamp;
  updatedByEmail: string; // lowercased
}

/** Firestore: docs/{docId}/versions/{versionId} (immutable) */
export interface DocVersion {
  versionId: string; // "v1", "v2", ...
  label: string; // human label, defaults to versionId
  entry: string; // entry file path within the bundle, e.g. "index.html"
  storagePrefix: string; // "docs/{docId}/{versionId}"
  fileCount: number;
  bytes: number;
  contentHash: string; // sha256 over the bundle; used for idempotent re-publish
  files?: string[]; // bundle relpaths (added 2026; missing on legacy versions). Enables client multi-file download without a Storage list.
  publishedAt: Timestamp;
}

/** A W3C-style text anchor scoped to a stable container id. */
export interface Anchor {
  elementId: string; // id of the nearest stable ancestor (section/article)
  exact: string; // the selected text (TextQuoteSelector.exact)
  prefix: string; // TextQuoteSelector.prefix
  suffix: string; // TextQuoteSelector.suffix
  start: number; // TextPositionSelector.start (fallback, relative to elementId root)
  end: number; // TextPositionSelector.end
}

/** Firestore: docs/{docId}/versions/{versionId}/annotations/{annId} */
export interface Annotation {
  annId: string;
  anchor: Anchor;
  authorUid: string;
  authorEmail: string; // lowercased
  color: string; // brand highlight, default "#F6DFA4"
  resolved: boolean;
  createdAt: Timestamp;
}

/** Firestore: .../annotations/{annId}/comments/{commentId} */
export interface Comment {
  commentId: string;
  body: string;
  authorUid: string;
  authorEmail: string; // lowercased
  createdAt: Timestamp;
}

export const HIGHLIGHT_COLOR = "#F6DFA4"; // brand "butter" warm-highlight accent
export const HIGHLIGHT_BORDER = "#E8D9A8"; // pale butter — subtle highlight outline (no fill)
export const ALLOWED_DOMAIN = "cellery.ai";

/** Sharing roles a doc owner can grant (CONTRACTS §2 /access). */
export type Role = "viewer" | "editor";
